import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import { EmployeeModel, IEmployeesDto } from "../shared";

export const employeesApiSlice = createApi({
  reducerPath: "api-employees",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  baseQuery,
  endpoints: (builder) => ({
    fetchEmployeesWithDeleted: builder.query<EmployeeModel[], void>({
      queryFn: pagedQueryFn<IEmployeesDto, EmployeeModel[]>(
        { url: "EmployeesV2", params: { includeDeleted: true } },
        (dtos) => {
          safeSortEmployeesByName(dtos);
          return dtos.map((dto) => {
            const model = new EmployeeModel(dto.id);
            model.updateFromDto(dto);
            return model;
          });
        }
      ),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: EmployeeModel[] }) => data,
  filterNotDeleted<EmployeeModel>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: EmployeeModel[] }) => currentData,
  filterNotDeleted<EmployeeModel>
);

const useEmployeesEndpoint =
  employeesApiSlice.endpoints.fetchEmployeesWithDeleted.useQuery;

type EmployeesQueryOptions = QuerySubscriptionOptions<typeof useEmployeesEndpoint>;

export function useFetchEmployees(options?: EmployeesQueryOptions) {
  return useEmployeesEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchEmployeesWithDeleted(options?: EmployeesQueryOptions) {
  return useEmployeesEndpoint(undefined, options);
}

export const useLazyFetchEmployeesWithDeleted =
  employeesApiSlice.endpoints.fetchEmployeesWithDeleted.useLazyQuery;

function safeSortEmployeesByName(dtos: IEmployeesDto[]): void {
  dtos.sort((a, b) => {
    try {
      if (a === null || b === null) {
        return 0; // never happened (insert your breakpoint here)
      }
      if (a.fullName === null) {
        a.fullName = a.lastName + " " + a.firstName; // "Тестовый 3 Пользователь 3"
      }
      if (b.fullName === null) {
        b.fullName = b.lastName + " " + b.firstName; // "Тестовый 2 Пользователь 2"
      }
      return a.fullName.localeCompare(b.fullName);
    } catch (_error) {
      return 0;
    }
  });
}
